import StandardQueryTable from "@/components/StandardQueryTable";
import { useTranslation } from "react-i18next";
import { Module, PATH_USER } from "@/constants";
import { displayUser } from "@/schemas/user";
import { fetchUserList } from "@/services/users";
import { DisplayUser } from "@/types/user";
import { useMemo } from "react";
import { Row } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import UserBadge from "./UserBadge";
import { generatePath } from "react-router-dom";
import TaxStatus from "@/components/TaxStatus";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { TAX_STATUS } from "@/schemas/commonFields/taxStatus";
import { SYSTEM_ROLE_CODE } from "@/schemas/user";

const UsersList = () => {
  const { t } = useTranslation();

  const columnOverrides = useMemo(
    () => ({
      is_active: {
        id: "is_active",
        cell: ({ row }: { row: Row<DisplayUser> }) => (
          <Badge variant={row.original.is_active ? "secondary" : "destructive"}>
            {row.original.is_active
              ? t("users.status.active")
              : t("users.status.inactive")}
          </Badge>
        ),
      },
      system_role_name: {
        id: "system_role_name",
        cell: ({ row }: { row: Row<DisplayUser> }) => (
          <span>{row.original.system_role_name}</span>
        ),
      },
      contractor_is_active: {
        id: "contractor_is_active",
        cell: ({ row }: { row: Row<DisplayUser> }) => {
          if (!row.original.contractor_id) return null;
          return <UserBadge isActive={row.original.contractor_is_active} />;
        },
      },
      tax_status: {
        id: "tax_status",
        cell: ({ row }: { row: Row<DisplayUser> }) => (
          <TaxStatus value={row.original.tax_status} />
        ),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: TAX_STATUS,
              translationKey: "taxStatus",
            },
          },
        },
      },
      system_role_code: {
        id: "system_role_code",
        cell: ({ row }: { row: Row<DisplayUser> }) =>
          row.original.system_role_code
            ? t(`userRole.${row.original.system_role_code}`)
            : null,
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: Object.values(SYSTEM_ROLE_CODE),
              translationKey: "userRole",
            },
          },
        },
      },
    }),
    [t],
  );

  return (
    <StandardQueryTable<DisplayUser>
      schema={displayUser}
      name="users"
      fetchList={fetchUserList}
      pathBuilder={(row) => generatePath(PATH_USER, { userId: row.id })}
      tableFeatures={{
        exportSettings: { moduleName: Module.Users },
      }}
      columnOverrides={columnOverrides}
    />
  );
};

export default UsersList;
